import React, {useMemo} from 'react';
import {PathUtility} from 'src/shared/constants/PathBuilder';

import {Menu} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {Title} from 'src/design/atoms/Title';
import type {MRT_ColumnDef} from 'mantine-react-table';
import type {
  PersonnelResponse,
  UserReferencedFormResponse,
} from 'src/api/ProactApiClient';
import {
  FormStateEnum,
  FormSubmissionScopeEnum,
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {Pill} from 'src/design/atoms/Pill';
import {NoticePopover} from 'src/design/molecules/NoticePopover';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {PermissionGuard} from 'src/app/security/authorization/PermissionGuard';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {getFormPillConfig} from 'src/app/forms/formHelpers';
import {useGetUserReferencedFormsV2Query} from 'src/redux/queries/form';
import {usePersonnelPermissions} from 'src/shared/auth/permissions/usePersonnelPermissions';
import {useFormSubmissionExport} from 'src/shared/hooks/useFormSubmissionExport';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface RelatedFormsTabProps {
  item?: PersonnelResponse;
}

export const RelatedFormsTab: React.FC<RelatedFormsTabProps> = ({item}) => {
  const navigate = useAppNavigate();
  const personnelPerms = usePersonnelPermissions();

  const columns = useMemo<MRT_ColumnDef<UserReferencedFormResponse>[]>(
    () => [
      {
        accessorKey: 'identifier',
        header: 'Identifier',
      },
      {
        accessorKey: 'formName',
        header: 'Form Name',
      },
      {
        accessorKey: 'fieldLabel',
        header: 'Field',
      },
      {
        accessorKey: 'formStatus',
        header: 'Status',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: [
            {value: String(FormStateEnum.Draft), label: 'Draft'},
            {value: String(FormStateEnum.Published), label: 'Published'},
            {value: String(FormStateEnum.Failed), label: 'Failed'},
            {value: String(FormStateEnum.Invalid), label: 'Invalid'},
          ],
        },
        Cell: ({cell}) => {
          const state = cell.getValue<FormStateEnum>();
          const pillConfig = getFormPillConfig(state);
          return <Pill text={pillConfig[0].text} color={pillConfig[0].color} />;
        },
      },
      {
        accessorKey: 'createdBy',
        header: 'Created By',
      },
      {
        accessorKey: 'createdAt',
        header: 'Created Date',
        Cell: ({cell}) => (
          <>{cell.getValue<Date | undefined>()?.toString().toShortDate()}</>
        ),
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier: 'user-related-forms',
    columns: columns,
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: [{id: 'formName', desc: false}],
    defaultShowColumnFilters: true,
  });
  const {start, length, filters, filterFns, sorting} = tableState;

  const {data, isLoading, isFetching, isError, refetch} =
    useGetUserReferencedFormsV2Query(
      {
        start: start,
        length: length,
        filters: filters,
        filterFns: filterFns,
        sorting: sorting,
        userId: item?.id || 0,
      },
      {skip: !item?.id || !personnelPerms.relatedForms}
    );

  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const {handleExport, isExporting} = useFormSubmissionExport(
    FormSubmissionScopeEnum.UserReferenced,
    {userId: item?.id},
    {filters, filterFns, sorting}
  );

  const handleNavigateToForm = (row: UserReferencedFormResponse) =>
    navigate(
      PathUtility.FormSubmission(row.formDefinitionId, row.formId, 'content')
    );

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      columns: columns,
      data: rows,
      renderRowActionMenuItems: ({row}) => (
        <>
          <Menu.Item onClick={() => handleNavigateToForm(row.original)}>
            View Form
          </Menu.Item>
        </>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Actions>
            <PermissionGuard
              module={ModuleEnum.Forms}
              operation={PermissionOperationEnum.Export}>
              <TableToolbar.Action
                variant="export"
                onClick={handleExport}
                loading={isExporting}
              />
            </PermissionGuard>
          </TableToolbar.Actions>
        </TableToolbar>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  if (!personnelPerms.relatedForms) {
    return (
      <ContentWrapper>
        <Title pb="lg">Related Forms</Title>
        <Text>You do not have permission to view related forms.</Text>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper>
      <Stack gap="xs">
        <Title>Related Forms</Title>
        <NoticePopover
          label="Permissions affecting data"
          title="Permissions Affecting Data"
          description="This list only shows form submissions you have permission to access."
          secondaryText="Contact your administrator if you need access to additional forms."
        />
        <Box>{table}</Box>
      </Stack>
    </ContentWrapper>
  );
};
